import React, { useEffect, useMemo, useState } from "react";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import EmptyState from "../components/common/EmptyState";
import Modal from "../components/common/Modal";
import { agentApi } from "../lib/apiClient";
import { showApiError, showErrorAction, showSuccessAction } from "../lib/alerts";

const ROLE_OPTIONS = [
  { value: "booking_staff", label: "Booking Staff" },
  { value: "accountant", label: "Accountant" },
];

const emptyForm = {
  full_name: "",
  email: "",
  mobile: "",
  password: "",
  role: "booking_staff",
};

function roleLabel(role) {
  return String(role || "").replace(/_/g, " ");
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);

  const activeCount = useMemo(
    () => users.filter((user) => String(user.status || "active").toLowerCase() === "active").length,
    [users]
  );

  async function loadUsers() {
    const payload = await agentApi.get("/travel-partner/users");
    const data = payload?.data || payload || [];
    setUsers(Array.isArray(data) ? data : []);
  }

  useEffect(() => {
    let alive = true;
    async function run() {
      try {
        setLoading(true);
        await loadUsers();
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_USERS");
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
  }, []);

  async function createUser(event) {
    event.preventDefault();
    try {
      setSaving(true);
      await agentApi.post("/travel-partner/users", { body: form });
      setForm(emptyForm);
      await loadUsers();
      showSuccessAction("CREATE_USER");
    } catch (saveError) {
      showApiError(saveError, "CREATE_USER");
    } finally {
      setSaving(false);
    }
  }

  function openEdit(user) {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      password: "",
      role: user.role === "accountant" ? "accountant" : "booking_staff",
    });
  }

  function closeEdit() {
    if (editSaving) return;
    setEditingUser(null);
    setEditForm(emptyForm);
  }

  async function saveEdit(event) {
    event.preventDefault();
    if (!editingUser) return;
    if (!String(editForm.full_name || "").trim()) {
      showErrorAction("REQUIRED_FIELDS");
      return;
    }
    if (editForm.password && String(editForm.password).length < 8) {
      showErrorAction("UPDATE_USER");
      return;
    }
    try {
      setEditSaving(true);
      const body = {
        full_name: editForm.full_name.trim(),
        mobile: editForm.mobile,
        role: editForm.role,
      };
      if (editForm.password) {
        body.password = editForm.password;
      }
      await agentApi.put(`/travel-partner/users/${editingUser.agency_user_id}`, { body });
      setEditingUser(null);
      setEditForm(emptyForm);
      await loadUsers();
      showSuccessAction("UPDATE_USER");
    } catch (updateError) {
      showApiError(updateError, "UPDATE_USER");
    } finally {
      setEditSaving(false);
    }
  }

  async function updateStatus(user, status) {
    try {
      await agentApi.put(`/travel-partner/users/${user.agency_user_id}`, {
        body: { status },
      });
      await loadUsers();
      showSuccessAction("UPDATE_USER");
    } catch (updateError) {
      showApiError(updateError, "UPDATE_USER");
    }
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Users"
          title="Agency users"
          items={[DASHBOARD_CRUMB, { label: "Users" }]}
        />

        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-24">
            <h5 className="mb-3">Add booking staff or accountant</h5>
            <form className="row g-3 align-items-end" onSubmit={createUser}>
              <div className="col-12 col-md-6 col-xl-3">
                <label className="form-label" htmlFor="user-name">Full name</label>
                <input
                  id="user-name"
                  className="form-control"
                  value={form.full_name}
                  onChange={(event) => setForm((current) => ({ ...current, full_name: event.target.value }))}
                  required
                />
              </div>
              <div className="col-12 col-md-6 col-xl-3">
                <label className="form-label" htmlFor="user-email">Email</label>
                <input
                  id="user-email"
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                  required
                />
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="user-mobile">Mobile</label>
                <input
                  id="user-mobile"
                  className="form-control"
                  value={form.mobile}
                  onChange={(event) => setForm((current) => ({ ...current, mobile: event.target.value }))}
                />
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="user-role">Role</label>
                <select
                  id="user-role"
                  className="form-select"
                  value={form.role}
                  onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-md-6 col-xl-2">
                <label className="form-label" htmlFor="user-password">Password</label>
                <input
                  id="user-password"
                  type="password"
                  className="form-control"
                  minLength={8}
                  value={form.password}
                  onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                  required
                />
              </div>
              <div className="col-12 col-xl-2">
                <button type="submit" className="btn btn-primary w-100" disabled={saving}>
                  {saving ? "Saving..." : "Add user"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-24">
            {loading || users.length > 0 ? <h5 className="mb-3">Agency users · {activeCount} active</h5> : null}
            {loading ? <PageLoader message="Loading users..." /> : null}
            {!loading && users.length === 0 ? (
              <EmptyState
                variant="customers"
                title="No agency users yet"
                description="Add booking staff or an accountant. They can sign in with this email and cannot approve bookings or payments."
              />
            ) : null}
            {!loading && users.length > 0 ? (
              <div className="table-responsive">
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => {
                      const active = String(user.status || "active").toLowerCase() === "active";
                      return (
                        <tr key={user.agency_user_id}>
                          <td>{user.full_name}</td>
                          <td className="text-break">{user.email}</td>
                          <td><StatusBadge value={user.role}>{roleLabel(user.role)}</StatusBadge></td>
                          <td><StatusBadge value={user.status || "active"} /></td>
                          <td className="text-end">
                            <div className="d-inline-flex flex-wrap justify-content-end gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => openEdit(user)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => updateStatus(user, active ? "inactive" : "active")}
                              >
                                {active ? "Disable" : "Enable"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <Modal
        open={Boolean(editingUser)}
        title="Edit agency user"
        onClose={closeEdit}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" disabled={editSaving} onClick={closeEdit}>
              Cancel
            </button>
            <button type="submit" form="edit-agency-user-form" className="btn btn-primary" disabled={editSaving}>
              {editSaving ? "Saving..." : "Save changes"}
            </button>
          </>
        )}
      >
        <form id="edit-agency-user-form" className="row g-3" onSubmit={saveEdit}>
          <div className="col-12">
            <label className="form-label" htmlFor="edit-user-name">Full name</label>
            <input
              id="edit-user-name"
              className="form-control"
              value={editForm.full_name}
              onChange={(event) => setEditForm((current) => ({ ...current, full_name: event.target.value }))}
              required
              disabled={editSaving}
            />
          </div>
          <div className="col-12">
            <label className="form-label" htmlFor="edit-user-email">Email</label>
            <input
              id="edit-user-email"
              type="email"
              className="form-control"
              value={editForm.email}
              disabled
              readOnly
            />
            <div className="form-text">Email cannot be changed. Disable this user and add a new one if the login email must change.</div>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="edit-user-mobile">Mobile</label>
            <input
              id="edit-user-mobile"
              className="form-control"
              value={editForm.mobile}
              onChange={(event) => setEditForm((current) => ({ ...current, mobile: event.target.value }))}
              disabled={editSaving}
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label" htmlFor="edit-user-role">Role</label>
            <select
              id="edit-user-role"
              className="form-select"
              value={editForm.role}
              onChange={(event) => setEditForm((current) => ({ ...current, role: event.target.value }))}
              disabled={editSaving}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="col-12">
            <label className="form-label" htmlFor="edit-user-password">New password</label>
            <input
              id="edit-user-password"
              type="password"
              className="form-control"
              minLength={8}
              value={editForm.password}
              onChange={(event) => setEditForm((current) => ({ ...current, password: event.target.value }))}
              disabled={editSaving}
              autoComplete="new-password"
              placeholder="Leave blank to keep the current password"
            />
          </div>
        </form>
      </Modal>
    </AgentLayout>
  );
}
